/**
 * AnalyticsOptOut - small always-visible privacy control
 *
 * Anonymous analytics runs by default (Do Not Track is respected by the
 * Mixpanel config); this control makes opting out discoverable without a
 * consent banner. Hidden on /admin, and on /embed, where the chat panel
 * shows PrivacyControl instead (as does the compudopt.org widget).
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import mixpanelService from '../services/MixpanelService';
import { trackConsentAccepted, trackConsentDeclined } from '../services/MixpanelEvents';

const LABELS = {
  en: {
    on: 'Analytics: on',
    off: 'Analytics: off',
    dnt: 'Analytics off (browser Do Not Track)',
    title: 'Anonymous usage analytics - no personal data or message content. Click to toggle.',
  },
  es: {
    on: 'Analítica: activada',
    off: 'Analítica: desactivada',
    dnt: 'Analítica desactivada (No rastrear del navegador)',
    title: 'Analítica de uso anónima - sin datos personales ni contenido de mensajes. Haz clic para cambiar.',
  },
};

const baseStyle = {
  position: 'fixed',
  bottom: '8px',
  left: '12px',
  zIndex: 900,
  fontSize: '11px',
  lineHeight: 1.4,
  color: 'var(--text-tertiary, #9CA3AF)',
  background: 'transparent',
  border: 'none',
  padding: '4px',
  opacity: 0.75,
};

const AnalyticsOptOut = ({ language = 'en' }) => {
  const location = useLocation();
  const [optedOut, setOptedOut] = useState(() => mixpanelService.hasOptedOut());

  // Re-read after Mixpanel initializes (parent effect runs after this one),
  // so a persisted opt-out from a previous visit is reflected.
  useEffect(() => {
    const id = setTimeout(() => setOptedOut(mixpanelService.hasOptedOut()), 500);
    return () => clearTimeout(id);
  }, []);

  // Normalize trailing slashes so /admin/ and /embed/ are excluded too
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  if (normalizedPath === '/embed' || normalizedPath === '/admin') {
    return null;
  }

  const t = LABELS[language] || LABELS.en;
  const dntEnabled =
    typeof navigator !== 'undefined' &&
    (navigator.doNotTrack === '1' || window.doNotTrack === '1');

  if (dntEnabled) {
    return <div style={baseStyle}>{t.dnt}</div>;
  }

  const handleToggle = () => {
    if (optedOut) {
      // Wrapper opts in first, then tracks - order matters
      trackConsentAccepted({ version: '1.0', trigger: 'settings' });
      setOptedOut(false);
    } else {
      // Wrapper tracks first, then opts out - an event after opt-out is dropped
      trackConsentDeclined({ version: '1.0', trigger: 'settings' });
      setOptedOut(true);
    }
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      title={t.title}
      aria-label={t.title}
      style={{ ...baseStyle, cursor: 'pointer', textDecoration: 'underline' }}
    >
      {optedOut ? t.off : t.on}
    </button>
  );
};

export default AnalyticsOptOut;
